import { fetchNotifications, fetchUnreadCount, markNotificationRead, NotificationItem } from "./notifications-api";
import {
    clearNotificationList,
    renderNotificationList,
    setLoading,
    showEmpty,
    updateBadge,
    NotificationListClickPayload,
} from "./notifications-render.js";

interface NotificationsControllerElements {
    triggerEl: HTMLElement;
    popoverEl: HTMLElement;
    loadingEl: HTMLElement;
    listEl: HTMLElement;
    emptyEl: HTMLElement;
    badgeEl: Element | null;
    overlayEl?: HTMLElement | null;
}

export interface NotificationsController {
    close(): void;
    open(): Promise<void>;
    toggle(): Promise<void>;
    updateUnreadBadge(): Promise<void>;
}

export function createNotificationsController({
    triggerEl: _triggerEl,
    popoverEl,
    loadingEl,
    listEl,
    emptyEl,
    badgeEl,
    overlayEl,
}: NotificationsControllerElements): NotificationsController {
    let isOpen = false;
    let isLoading = false;

    const close = (): void => {
        isOpen = false;
        popoverEl.hidden = true;
        if (overlayEl instanceof HTMLElement) overlayEl.hidden = true;
        setLoading(loadingEl, false);
    };

    const updateUnreadBadge = async (): Promise<void> => {
        try {
            const count = await fetchUnreadCount();
            updateBadge(badgeEl, count);
        } catch (_error) {
            // Silent fail
        }
    };

    const open = async (): Promise<void> => {
        isOpen = true;
        popoverEl.hidden = false;
        if (overlayEl instanceof HTMLElement) overlayEl.hidden = false;
        if (isLoading) return;
        isLoading = true;

        try {
            clearNotificationList(listEl);
            listEl.hidden = true;
            showEmpty(emptyEl, { visible: false });
            setLoading(loadingEl, true);

            const { ok, items } = await fetchNotifications();

            if (!ok) {
                showEmpty(emptyEl, { visible: true, translateKey: "notifications_load_failed" });
                return;
            }

            if (!items.length) {
                showEmpty(emptyEl, { visible: true, translateKey: "notifications_empty" });
                return;
            }

            const unreadCount = items.reduce((accumulator: number, item: NotificationItem) => {
                return accumulator + (item?.read_at ? 0 : 1);
            }, 0);
            updateBadge(badgeEl, unreadCount);

            renderNotificationList(listEl, items, {
                onItemClick: async ({ notificationId, itemEl }: NotificationListClickPayload) => {
                    if (!Number.isFinite(notificationId) || notificationId <= 0) return;

                    try {
                        await markNotificationRead(notificationId);
                    } catch (_error) {
                        // Silent fail
                    } finally {
                        itemEl?.classList?.remove?.("is-unread");
                        await updateUnreadBadge();
                    }
                },
            });

            showEmpty(emptyEl, { visible: false });
            listEl.hidden = false;
        } catch (_error) {
            showEmpty(emptyEl, { visible: true, translateKey: "notifications_load_failed" });
        } finally {
            setLoading(loadingEl, false);
            isLoading = false;
        }
    };

    const toggle = async (): Promise<void> => {
        if (isOpen) close();
        else await open();
    };

    return {
        close,
        open,
        toggle,
        updateUnreadBadge,
    };
}
